import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { TableCard } from "@ui/internal/components/card";
import FilterPopover from "@ui/internal/components/filter-popover";
import { usePathname } from "@utils/hook/index.hook";
import { QueryMetaData } from "@utils/interface/index.interface";
import { HomeCherishMomentData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { useMetaStore } from "@utils/states/index.state";
import { format } from "date-fns";
import React, { useEffect, useMemo } from "react";
import { Button } from "@components/ui/button";
import { AlertCircle } from "lucide-react";
import { useSearchParams } from "react-router";
import { ColumnList } from "./column/index.column";
import CherishMomentForm from "./form/index.form";

type Props = {};

const index: React.FC<Props> = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, date } = useMetaStore();
  const pathname = usePathname();

  const { data, isPending, refetch, error } = useListData<
    QueryMetaData<HomeCherishMomentData, any>
  >({
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      search,
      created_at__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
      created_at__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
      ordering: searchParams.get("ordering") || "-created_at",
      is_show: searchParams.get("status") || "",
    },
    endpoint: endpoint["home-cherish-moments"],
    key: `${pathname}-listing`,
    window: true,
  });

  useEffect(() => {
    if (
      //@ts-ignore
      error?.status === 404 &&
      error?.data?.detail === "Invalid page."
    ) {
      searchParams.set("tpage", "1");
      setSearchParams(searchParams);
    }
  }, [error]);

  const errorMessage = useMemo(() => {
    if (!error) return null;
    //@ts-ignore
    if (error?.response) {
      //@ts-ignore
      return `${error.response.status}: ${JSON.stringify(error.response.data)}`;
    }
    return (error as any).message || "Failed to load home cherish moments";
  }, [error]);

  const groupRefetch = () => {
    refetch();
  };

  const { mutate } = usePostData();
  const toggleActiveStatus = (id: string | number, state: boolean) => {
    mutate(
      {
        endpoint: endpoint["home-cherish-moments"],
        data: {
          is_show: state,
          id,
        },
        method: METHODS.PATCH,
      },
      {
        onSuccess: () => {
          if (data) {
            const newItems = data.data.results.map((item) =>
              item.id === id ? { ...item, is_show: state } : item
            );
            data.data.results = newItems;
          }
        },
        onError: (data) => {
          console.log(data);
        },
      }
    );
  };

  const columns = ColumnList({ refetch: groupRefetch, toggleActiveStatus });

  const rows = useMemo(() => data?.data?.results ?? [], [data]);

  return (
    <>
      {errorMessage && (
        <div className="m-4 p-4 border border-red-200 rounded-lg bg-red-50 text-red-700 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Failed to load data</p>
            <p className="text-sm opacity-90">{errorMessage}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}
      <TableCard
        bulkActions
        formId={pathname + "cherish-moment"}
        table_content={{
          mode: "default",
          type: pathname,
        }}
        form={<CherishMomentForm />}
        showOrientationSecond
        tableHeaderData={[
          {
            id: 1,
            name: "All",
            count: data?.data?.count || 0,
          },
        ]}
        inject_ui_table_actions={
          <FilterPopover
            filters={[
              {
                label: "Status",
                searchKey: "status",
                radio: true,
                defaultOptions: [
                  {
                    label: "Featured",
                    value: "true",
                  },
                  {
                    label: "Non-Featured",
                    value: "false",
                  },
                ],
              },
            ]}
          />
        }
        sortableFilter={{
          search,
          ordering: searchParams.get("ordering") || "-created_at",
          is_show: searchParams.get("status") || "",
          section: "",
        }}
        endpoint={endpoint["home-cherish-moments"]}
        columns={columns}
        rows={rows}
        refetch={groupRefetch}
        isPending={isPending}
        count={data?.data?.count ?? 10}
      />
    </>
  );
};

export default index;
